// Turning what a person did into something the server will take. Lifted from
// the Andon app's `lib/capture.ts`; the two refusals it can utter are TR keys
// handed in by the caller, so this stays free of React.
//
// A gloved operator beside a running line does not type. They photograph, or
// they speak, or they hold a drawing up to the camera — so the composer needs
// a way to get a file, a clip or a recording into base 64 without a modal
// detour and without a multi-megapixel JPEG ever entering React state.
//
// Everything here returns BARE base64 with no `data:` prefix, because that is
// what Odoo stores and what the BFF's validator sniffs. The mimetype is the
// server's decision from the bytes, never from what this file claims.

const MAX_ATTACH_BYTES = 100 * 1024 * 1024;

export interface CapturedFile {
  name: string;
  /** base64, no data: prefix. */
  data: string;
  /** Bytes, decoded — what the row's subtitle shows and the guard checks. */
  size: number;
  /** Seconds, for a clip. Null for anything that is not one. */
  seconds: number | null;
}

/** The sentences a refused capture is made of, in the reader's language. */
export interface CaptureWords {
  /** "That file could not be read." */
  unreadable: string;
  /** Between the name and the size: "rack.mp4 is 214.0 MB." */
  is: string;
  /** "The limit is 100 MB." */
  limit: string;
  /** "That clip" — the name a recording is refused under. */
  thatClip: string;
}

/** 1.2 MB, 940 kB, 14 kB. What a person recognises as "is this too big". */
export function fileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${bytes} B`;
}

/** 0:14. Clips are short by construction, so mm:ss is the whole vocabulary. */
export function clipLength(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * A Blob, as base64.
 *
 * FileReader rather than `arrayBuffer()` + manual encoding: it is the one path
 * that does not build a megabyte-long JS string a character at a time, which
 * is what killed the WebView on the Zebra tablets when this was tried the
 * obvious way.
 */
export function blobToBase64(blob: Blob, words: CaptureWords): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(words.unreadable));
    reader.onload = () => {
      const url = String(reader.result ?? '');
      resolve(url.slice(url.indexOf(',') + 1));
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * A picked file, checked against the same ceiling the server enforces.
 *
 * Checked HERE as well, and that is not belt and braces: a 40MB video refused
 * by the body parser comes back as a transport error with no sentence anybody
 * can act on, where this comes back as "that file is too big" before the
 * upload starts.
 */
export async function captureFile(file: File, words: CaptureWords, seconds: number | null = null): Promise<CapturedFile> {
  if (file.size > MAX_ATTACH_BYTES) {
    throw new Error(`${file.name} ${words.is} ${fileSize(file.size)}. ${words.limit}`);
  }
  return { name: file.name || 'file', data: await blobToBase64(file, words), size: file.size, seconds };
}

/** A recording, named for what it is rather than for a timestamp nobody reads. */
export async function captureBlob(blob: Blob, name: string, seconds: number | null, words: CaptureWords): Promise<CapturedFile> {
  if (blob.size > MAX_ATTACH_BYTES) {
    throw new Error(`${words.thatClip} ${words.is} ${fileSize(blob.size)}. ${words.limit}`);
  }
  return { name, data: await blobToBase64(blob, words), size: blob.size, seconds };
}

/**
 * Whether this browser can record at all.
 *
 * Asked before the control is drawn rather than after it is pressed: a Record
 * button that fails on tap is worse than one that was never offered, and the
 * Zebra tablets and iOS Safari disagree about which containers exist.
 */
export function canRecord(): boolean {
  return typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
}

/**
 * The first container this browser will actually produce.
 *
 * Chrome gives WebM/Opus, Safari gives MP4/AAC, and asking for the wrong one
 * throws at construction. Empty string means "you decide", which every engine
 * accepts and which is the honest last resort — the server sniffs the bytes
 * either way.
 */
export function pickMimeType(kind: 'audio' | 'video'): string {
  const candidates =
    kind === 'audio'
      ? ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg']
      : ['video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
  for (const type of candidates) {
    if (MediaRecorder.isTypeSupported?.(type)) return type;
  }
  return '';
}

/** The filename extension that matches what `pickMimeType` chose. */
export function extensionFor(mimeType: string): string {
  if (mimeType.includes('mp4')) return 'mp4';
  if (mimeType.includes('ogg')) return 'ogg';
  return 'webm';
}
